import React, { useState } from 'react';
import { addMonths, format, startOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { Sparkles } from 'lucide-react';
import { usePerfil } from '../../context/PerfilContext';
import { useConfirmar } from '../../context/ConfirmarContext';
import { LIMITES_GRATIS, MESES_REGALO, NOMBRE_LIMITE, ClaveLimite } from '../../config/planes';

const fecha = (d: Date) => format(d, "d 'de' MMMM 'de' yyyy", { locale: es });

/**
 * El interruptor general de myarinn Plus (`configuracionGlobal/plus`). Hasta
 * que se activa, todo el mundo usa la app sin límites. Al activarlo, quien ya
 * tenía cuenta antes de esa fecha recibe MESES_REGALO meses de Plus.
 */
export const AjustesPlus: React.FC = () => {
  const { configPlus, guardarConfigPlus } = usePerfil();
  const confirmar = useConfirmar();
  const [dia, setDia] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [error, setError] = useState('');

  const activar = async () => {
    const desde = startOfDay(new Date(`${dia}T00:00:00`));
    const hasta = addMonths(desde, MESES_REGALO);
    if (!(await confirmar({
      titulo: '¿Activar los límites del plan gratis?',
      mensaje: `Desde el ${fecha(desde)}, las cuentas nuevas tendrán los límites del plan gratis y ` +
        `Préstamos pasará a ser de Plus. Quien ya tenga cuenta antes de ese día tendrá Plus gratis ` +
        `hasta el ${fecha(hasta)}.`,
      accion: 'Activar',
      destructiva: false,
    }))) return;
    setError('');
    try { await guardarConfigPlus({ limitesDesde: desde, regaloHasta: hasta }); }
    catch (e) { console.error(e); setError('No se ha podido guardar.'); }
  };

  const quitar = async () => {
    if (!(await confirmar({
      titulo: '¿Quitar los límites?',
      mensaje: 'Todo el mundo vuelve a usar la app sin límites. Lo pagado no se toca.',
      accion: 'Quitar',
    }))) return;
    setError('');
    try { await guardarConfigPlus(null); }
    catch (e) { console.error(e); setError('No se ha podido guardar.'); }
  };

  return (
    <section>
      <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400" /> myarinn Plus
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
        Plan gratis: {(Object.keys(LIMITES_GRATIS) as ClaveLimite[])
          .filter(k => LIMITES_GRATIS[k] > 0)
          .map(k => `${LIMITES_GRATIS[k]} ${NOMBRE_LIMITE[k][LIMITES_GRATIS[k] === 1 ? 0 : 1]}`)
          .join(', ')}; Inversiones y Préstamos solo en Plus.
      </p>

      {configPlus ? (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="flex-1 text-sm text-gray-800 dark:text-gray-200">
            {configPlus.limitesDesde > new Date() ? 'Los límites empiezan' : 'Límites activos desde'} el{' '}
            <strong>{fecha(configPlus.limitesDesde)}</strong>. Regalo a los usuarios de antes hasta el{' '}
            <strong>{fecha(configPlus.regaloHasta)}</strong>.
          </p>
          <button type="button" onClick={quitar}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-semibold
                       text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Quitar límites
          </button>
        </div>
      ) : (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <p className="flex-1 text-sm text-gray-800 dark:text-gray-200">
            Los límites no están activos: todo el mundo tiene la app completa.
          </p>
          <input type="date" value={dia} onChange={e => setDia(e.target.value)} aria-label="Desde qué día"
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" />
          <button type="button" onClick={activar} disabled={!dia}
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold disabled:opacity-50">
            Activar límites
          </button>
        </div>
      )}
      {error && <p className="text-sm text-red-600 dark:text-red-400 mt-2">{error}</p>}
    </section>
  );
};
